import { Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { LeadCard } from '../components/kanban/LeadCard';
import { MoveLeadSheet } from '../components/kanban/MoveLeadSheet';
import { ProjectNotesPanel } from '../components/project/ProjectNotesPanel';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { useFlowchart } from '../hooks/use-flowchart';
import { useLeads, useMoveLead, type LeadSummary } from '../hooks/use-leads';
import { useProject } from '../hooks/use-projects';
import { useAuth } from '../lib/auth-context';

interface ProjectKanbanShape {
  activeFlowchartId?: string;
  name?: string;
  notes?: string;
  members?: Array<{ userId: string; projectRole: 'manager' | 'member' | 'viewer' }>;
}

export function KanbanPage(): JSX.Element {
  const { id: projectId } = useParams<{ id: string }>();
  const project = useProject(projectId);
  const projectData = project.data as ProjectKanbanShape | undefined;
  const auth = useAuth();
  const flowchartId = projectData?.activeFlowchartId;
  const flowchart = useFlowchart(flowchartId);
  const leads = useLeads(projectId);
  const { t } = useTranslation();

  const stagesById = useMemo(() => {
    const map: Record<string, NonNullable<typeof flowchart.data>['stages'][number]> = {};
    for (const s of flowchart.data?.stages ?? []) map[s.id] = s;
    return map;
  }, [flowchart.data]);

  const leadsByStage = useMemo(() => {
    const map: Record<string, LeadSummary[]> = {};
    for (const lead of leads.data?.items ?? []) {
      (map[lead.currentStageId] ??= []).push(lead);
    }
    return map;
  }, [leads.data]);

  const [movingLead, setMovingLead] = useState<LeadSummary | null>(null);
  const move = useMoveLead(movingLead?.id ?? '', projectId ?? '');

  const outgoingTransitions = useMemo(() => {
    if (!movingLead) return [];
    return (flowchart.data?.transitions ?? []).filter(
      (tr) => tr.fromStageId === movingLead.currentStageId,
    );
  }, [movingLead, flowchart.data]);

  if (project.isLoading || flowchart.isLoading) {
    return <Skeleton className="h-64" />;
  }
  const stages = (flowchart.data?.stages ?? []).slice().sort((a, b) => a.position - b.position);

  const myMembership = projectData?.members?.find((m) => m.userId === auth.user?.id);
  const canEditNotes = auth.user?.role === 'MASTER' || myMembership?.projectRole === 'manager';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">
            {projectData?.name ?? t('kanban.newLead')}
          </h1>
          <p className="text-sm text-surface-500">
            {t('kanban.leadCount', { count: leads.data?.items.length ?? 0 })} ·{' '}
            {t('kanban.flowchartVersion', { version: flowchart.data?.flowchart.version ?? '?' })}
          </p>
        </div>
        <Button asChild>
          <Link to={`/projects/${projectId}/leads/new`}>
            <Plus className="h-4 w-4" /> {t('kanban.newLead')}
          </Link>
        </Button>
      </div>

      {projectId && (
        <ProjectNotesPanel
          projectId={projectId}
          notes={projectData?.notes ?? ''}
          canEdit={canEditNotes}
        />
      )}

      <div className="flex gap-3 overflow-x-auto pb-2">
        {stages.map((stage) => (
          <section
            key={stage.id}
            className="flex w-72 shrink-0 flex-col rounded-lg border border-surface-200 bg-white shadow-card"
          >
            <header
              className="flex items-center justify-between rounded-t-lg border-b border-surface-200 px-3 py-2.5"
              style={{
                backgroundImage: `linear-gradient(180deg, ${stage.color}10 0%, transparent 100%)`,
                borderLeft: `3px solid ${stage.color}`,
              }}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-sm font-semibold text-surface-800 truncate">{stage.name}</span>
              </div>
              <span className="ml-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-surface-100 px-1.5 text-[11px] font-semibold text-surface-700">
                {(leadsByStage[stage.id] ?? []).length}
              </span>
            </header>
            <div className="flex-1 space-y-2 bg-surface-50/40 p-2">
              {(leadsByStage[stage.id] ?? []).map((lead) => (
                <LeadCard
                  key={lead.id}
                  lead={lead}
                  slaHours={stage.slaHours}
                  onMove={() => setMovingLead(lead)}
                />
              ))}
              {!(leadsByStage[stage.id] ?? []).length && (
                <p className="px-2 py-3 text-xs italic text-surface-400">{t('kanban.noLeads')}</p>
              )}
            </div>
          </section>
        ))}
      </div>

      <MoveLeadSheet
        open={!!movingLead}
        onOpenChange={(o) => {
          if (!o) setMovingLead(null);
        }}
        leadTitle={movingLead?.title ?? ''}
        outgoingTransitions={outgoingTransitions}
        stagesById={stagesById}
        busy={move.isPending}
        onMove={async (input) => {
          await move.mutateAsync(input);
        }}
      />
    </div>
  );
}
